import { defineStore } from "pinia";
import axios from "axios";
import Cookies from "js-cookie";

// let fetchMePromise = null;

/**
 * Normalisasi objek hak akses agar memiliki kompatibilitas ganda:
 * Mendukung baik format baru ('create', 'read', 'update', 'delete', 'approval', 'import', 'export')
 * maupun format lama ('can_create', 'can_read', 'can_update', 'can_delete', dll).
 */
function normalizeAccess(rawAccess) {
  if (!rawAccess) return null;
  const normalized = { ...rawAccess };
  for (const [key, val] of Object.entries(rawAccess)) {
    if (!key.startsWith("can_")) {
      normalized[`can_${key}`] = val;
    } else {
      normalized[key.replace(/^can_/, "")] = val;
    }
  }
  return normalized;
}

// Cache promise in-flight untuk mencegah request HTTP ganda ke /me secara bersamaan
let inFlightFetchMe = null;

export const useAuthStore = defineStore("auth", {
  state: () => ({
    // Mengambil data user dari cookie jika sudah tersimpan sebelumnya
    user: Cookies.get("user") ? JSON.parse(Cookies.get("user")) : null,
    // Mengambil token autentikasi (saa_token) dari cookie
    token: Cookies.get("saa_token") || null,
    // Penyimpanan seluruh hak akses user per page_code dari endpoint /me
    // Format: { [page_code]: { read: true, can_read: true, create: true, can_create: true, ... } }
    privileges: {},
    isPrivilegesLoaded: false,
  }),
  getters: {
    // Mengecek apakah user saat ini dalam status login (memiliki token & data user)
    loggedIn: (state) => !!state.token && !!state.user,
    // Getter untuk mendapatkan data profil user yang sedang aktif
    currentUser: (state) => state.user,
    // Mengecek apakah user memiliki izin aksi tertentu pada suatu page_code
    hasAccess:
      (state) =>
      (pageCode, action = "read") => {
        if (!pageCode) return false;
        const pageAccess = state.privileges?.[pageCode];
        if (!pageAccess) return false;
        const cleanAction = String(action).toLowerCase().replace(/^can_/, "");
        return Boolean(pageAccess[cleanAction] ?? pageAccess[`can_${cleanAction}`]);
      },
    // Mengambil objek hak akses lengkap untuk page_code tertentu secara sinkron dari state
    getPrivilegeByCode: (state) => (pageCode) => {
      if (!pageCode) return null;
      return state.privileges?.[pageCode] || null;
    },
  },
  actions: {
    // Menyimpan atau memperbarui data user ke dalam state store
    setUser(data) {
      this.user = data;
    },

    /**
     * Menyimpan seluruh hak akses user ke store dengan normalisasi format.
     * @param {Object} privilegesMap Objek key-value { [pageCode]: accessObject }
     */
    setPrivileges(privilegesMap) {
      if (!privilegesMap || typeof privilegesMap !== "object") {
        this.privileges = {};
        this.isPrivilegesLoaded = true;
        return;
      }

      const normalizedMap = {};
      for (const [pageCode, access] of Object.entries(privilegesMap)) {
        normalizedMap[pageCode] = normalizeAccess(access);
      }
      this.privileges = normalizedMap;
      this.isPrivilegesLoaded = true;
    },

    /**
     * Mengambil profil user dan seluruh hak akses sekaligus dari endpoint GET /me.
     * Dilengkapi mekanisme deduplikasi agar pemanggilan simultan hanya mengeksekusi 1 HTTP request.
     * @param {boolean} force Jika true, abaikan cache memory dan ambil data terbaru dari server
     */
    async fetchMe(force = false) {
      // Jika data sudah dimuat dan tidak diminta refresh paksa, gunakan cache memory
      if (!force && this.isPrivilegesLoaded && this.user) {
        return { user: this.user, privileges: this.privileges };
      }

      // Jika request sedang berjalan, bagikan promise yang sama (cegah duplicate request)
      if (inFlightFetchMe) {
        return inFlightFetchMe;
      }

      inFlightFetchMe = (async () => {
        const backendUrl = process.env.VUE_APP_BACKEND_API_URL || window.location.origin;
        try {
          const currentToken = Cookies.get("saa_token") || this.token;
          if (currentToken && !this.token) {
            this.token = currentToken;
          }

          const headers = {};
          if (currentToken) {
            headers["Authorization"] = `Bearer ${currentToken}`;
          }

          const response = await axios.get(`${backendUrl}/me`, {
            headers,
            withCredentials: true,
          });

          const raw = response.data || {};
          const data = raw.data && typeof raw.data === "object" && (raw.data.user || raw.data.privileges || raw.data.privileges_list) ? raw.data : raw;
          const userData = data.user || raw.user;

          if (userData) {
            this.setUser(userData);
            Cookies.set("user", JSON.stringify(userData), { expires: 90, path: "/" });
          }

          const privs = data.privileges || raw.privileges;
          const privList = data.privileges_list || raw.privileges_list;

          if (privs) {
            this.setPrivileges(privs);
          } else if (Array.isArray(privList)) {
            const mapped = {};
            privList.forEach((item) => {
              if (item.pages_code) {
                mapped[item.pages_code] = item.access;
              }
            });
            this.setPrivileges(mapped);
          } else {
            this.isPrivilegesLoaded = true;
          }

          return { user: userData, privileges: this.privileges, raw };
        } catch (error) {
          console.error("Gagal memuat konteks sesi /me:", error);
          // Jika session expired atau unauthorized (401), bersihkan data sesi usang
          if (error?.response?.status === 401) {
            this.user = null;
            this.token = null;
            this.privileges = {};
            this.isPrivilegesLoaded = false;
            Cookies.remove("user");
            Cookies.remove("saa_token");
          }
          return null;
        }
      })().finally(() => {
        inFlightFetchMe = null;
      });

      return inFlightFetchMe;
    },

    /**
     * Mengambil hak akses (privilege) untuk page_code tertentu.
     * Membaca langsung dari in-memory cache yang dimuat dari /me tanpa HTTP request tambahan.
     */
    async getPrivilege(pageCode) {
      // Jika pageCode kosong atau tidak valid, kembalikan null
      if (!pageCode) return null;

      // 1. Cek apakah privilege untuk page_code ini sudah ada di memory
      if (this.privileges && this.privileges[pageCode]) {
        return this.privileges[pageCode];
      }

      // 2. Jika privileges belum pernah dimuat (misal direct page reload), panggil fetchMe sekali
      if (!this.isPrivilegesLoaded) {
        await this.fetchMe();
        if (this.privileges && this.privileges[pageCode]) {
          return this.privileges[pageCode];
        }
      }

      // 3. Fallback jika page_code tidak memiliki hak akses
      return this.privileges[pageCode] || null;
    },

    // Mengosongkan seluruh cache hak akses (misal saat logout atau switch user)
    clearPrivileges() {
      this.privileges = {};
      this.isPrivilegesLoaded = false;
    },

    // Proses logout: bersihkan session backend, token cookie, storage lokal, dan cache privilege
    async logout() {
      // 1. Kosongkan cache privilege yang tersimpan di store
      this.clearPrivileges();

      // 2. Ambil token dari cookie untuk keperluan request logout ke server
      const saaToken = Cookies.get("saa_token");
      const backendUrl = process.env.VUE_APP_BACKEND_API_URL || window.location.origin;

      try {
        // Jika ada token, panggil endpoint API logout di backend
        if (saaToken) {
          await axios.post(
            `${backendUrl}/logout`,
            {},
            {
              headers: { Authorization: `Bearer ${saaToken}` },
            },
          );
        }
      } catch (error) {
        // Log pesan jika API logout gagal, tetap lanjutkan proses pembersihan data lokal
        console.log("API logout error (proceed with local logout):", error.message);
      }

      // 3. Hapus cookie token dan data user
      Cookies.remove("saa_token");
      Cookies.remove("user");

      // 4. Bersihkan localStorage dan sessionStorage browser
      localStorage.clear();
      sessionStorage.clear();

      // 5. Redirect user ke halaman logout SSO / login
      const backendOrigin = new URL(backendUrl).origin;
      window.location.href = `${backendOrigin}/auth/logout`;
    },
  },
});
